import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TipoLineaMaterial, TipoTrabajo } from '@cmms/shared';
import { api, consulta, ErrorApi } from '../servicios/api.js';
import { Dialogo } from './piezas.js';
import { EditorPlan } from './plan-materiales-solicitud.js';

interface MaterialOt {
  id: string;
  nombre: string;
  tipo: TipoLineaMaterial;
  cantidad: number;
  nota: string | null;
  orden: number;
  marcado: boolean;
}

interface LineaPrevista {
  lineaOrigenId: string;
  nombre: string;
  tipo: TipoLineaMaterial;
  cantidad: number;
  nota: string | null;
  orden: number;
}

const ETIQUETA_TIPO: Record<TipoLineaMaterial, string> = {
  HERRAMIENTA: 'Herramientas',
  MATERIAL: 'Materiales',
};

/**
 * Materiales y herramientas de la OT. La primera vez que se abre, copia la lista
 * que corresponde al tipo de activo y al tipo de trabajo; después solo se marca.
 */
/**
 * Asignar materiales a mano a la OT, con el mismo editor que el plan de la solicitud:
 * buscar en el catálogo, poner cantidades y guardar. Se abre desde la sección.
 */
function EditorMaterialesOt({
  otId,
  inicial,
  alCerrar,
}: {
  otId: string;
  inicial: MaterialOt[];
  alCerrar: () => void;
}): JSX.Element {
  const lineas = inicial.map((m) => ({
    id: m.id,
    catalogoId: null,
    nombre: m.nombre,
    tipo: m.tipo,
    cantidad: m.cantidad,
    nota: m.nota,
    orden: m.orden,
  }));
  return (
    <Dialogo titulo="Materiales y herramientas de la orden" ancho={860} alCerrar={alCerrar}>
      <EditorPlan
        urlPlan={`/ordenes/${otId}/materiales`}
        clavePlan={['ot-materiales', otId]}
        activoId={null}
        editable
        iniciarEnEdicion
        inicial={lineas}
        alGuardar={alCerrar}
        sinPanel
      />
    </Dialogo>
  );
}

export function SeccionMaterialesOt({ otId, puedeMarcar }: { otId: string; puedeMarcar: boolean }): JSX.Element {
  const cliente = useQueryClient();
  const clave = ['ot-materiales', otId];
  const [editando, setEditando] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: clave,
    queryFn: async () => {
      const actuales = await api.get<MaterialOt[]>(`/ordenes/${otId}/materiales`);
      if (actuales.length > 0) return actuales;
      return api.post<MaterialOt[]>(`/ordenes/${otId}/materiales/generar`);
    },
  });

  const marcar = useMutation({
    mutationFn: ({ id, marcado }: { id: string; marcado: boolean }) =>
      api.patch<MaterialOt>(`/ordenes/${otId}/materiales/${id}`, { marcado }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: clave }),
  });

  if (isLoading) return <p className="campo-nota">Cargando materiales…</p>;
  if (error) {
    return <p className="campo-nota">{error instanceof ErrorApi ? error.message : 'No se pudieron cargar los materiales.'}</p>;
  }
  const botonEditar = puedeMarcar ? (
    <button type="button" className="boton secundario pequeno" onClick={() => setEditando(true)}>
      Editar materiales
    </button>
  ) : null;
  const editor = editando ? <EditorMaterialesOt otId={otId} inicial={data ?? []} alCerrar={() => setEditando(false)} /> : null;

  if (!data || data.length === 0) {
    return (
      <section className="panel" id="materiales">
        <div className="panel-cabecera" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <h2>Materiales y herramientas</h2>
          {botonEditar}
        </div>
        <div className="panel-cuerpo">
          <p className="campo-nota">Ninguna lista aplica a esta orden. Puedes asignar materiales a mano.</p>
        </div>
        {editor}
      </section>
    );
  }

  const grupos: TipoLineaMaterial[] = ['HERRAMIENTA', 'MATERIAL'];
  const marcados = data.filter((m) => m.marcado).length;

  return (
    <section className="panel" id="materiales">
      <div className="panel-cabecera" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <h2>Materiales y herramientas</h2>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="campo-nota">
            {marcados} de {data.length}
          </span>
          {botonEditar}
        </span>
      </div>
      <div className="panel-cuerpo">
        {grupos.map((tipo) => {
          const lineas = data.filter((m) => m.tipo === tipo);
          if (lineas.length === 0) return null;
          return (
            <div key={tipo} style={{ marginBottom: 12 }}>
              <div className="indicador-titulo">{ETIQUETA_TIPO[tipo]}</div>
              {lineas.map((m) => (
                <label
                  key={m.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', minHeight: 44, cursor: puedeMarcar ? 'pointer' : 'default' }}
                >
                  <input
                    type="checkbox"
                    checked={m.marcado}
                    disabled={!puedeMarcar || marcar.isPending}
                    onChange={(e) => marcar.mutate({ id: m.id, marcado: e.target.checked })}
                    style={{ width: 22, height: 22 }}
                  />
                  <span style={{ textDecoration: m.marcado ? 'line-through' : 'none', flex: 1 }}>
                    {m.nombre}
                    {m.nota ? <span className="campo-nota"> · {m.nota}</span> : null}
                  </span>
                  <span className="campo-nota">× {m.cantidad}</span>
                </label>
              ))}
            </div>
          );
        })}
      </div>
      {editor}
    </section>
  );
}

/**
 * Checklist de materiales/herramientas al crear una OT (§ pedido): previsualiza
 * la lista que le aplicaría al activo elegido para el tipo de trabajo elegido,
 * todo marcado por defecto — el creador desmarca lo que no hará falta. Lo que
 * quede marcado es con lo que la OT nace (`ot_materiales`), en vez de con todo
 * sin marcar a la espera de que el técnico lo revise desde cero.
 */
export function SeccionMaterialesPrevisualizados({
  activoId,
  tipoTrabajo,
  excluidos,
  alCambiarExcluidos,
}: {
  activoId: string;
  tipoTrabajo: TipoTrabajo;
  excluidos: Set<string>;
  alCambiarExcluidos: (actualizar: (anterior: Set<string>) => Set<string>) => void;
}): JSX.Element | null {
  const { data, isLoading, error } = useQuery({
    queryKey: ['listas-materiales', 'previsualizar', activoId, tipoTrabajo],
    queryFn: () => api.get<LineaPrevista[]>(`/listas-materiales/previsualizar${consulta({ activoId, tipoTrabajo })}`),
  });

  if (isLoading) return <p className="campo-nota">Cargando materiales y herramientas…</p>;
  // Sin acceso a listas de materiales, o sin lista aplicable: ninguno de los
  // dos es un error que haya que mostrar, así que la sección no aparece.
  if (error || !data || data.length === 0) return null;

  function alternar(lineaOrigenId: string, marcar: boolean): void {
    alCambiarExcluidos((anterior) => {
      const copia = new Set(anterior);
      if (marcar) copia.delete(lineaOrigenId);
      else copia.add(lineaOrigenId);
      return copia;
    });
  }

  const grupos: TipoLineaMaterial[] = ['HERRAMIENTA', 'MATERIAL'];
  const marcados = data.filter((l) => !excluidos.has(l.lineaOrigenId)).length;

  return (
    <div className="panel" style={{ marginTop: 16 }}>
      <div className="panel-cabecera">
        <h2>Materiales y herramientas</h2>
        <span className="campo-nota">
          {marcados} de {data.length}
        </span>
      </div>
      <div className="panel-cuerpo">
        <p className="campo-nota" style={{ marginTop: 0 }}>
          De la lista configurada para este tipo de activo y tipo de trabajo. Desmarque lo que no haga falta: con lo
          que quede marcado nace la orden, y quien la reciba podrá agregar algo más si faltó.
        </p>
        {grupos.map((tipo) => {
          const lineas = data.filter((l) => l.tipo === tipo);
          if (lineas.length === 0) return null;
          return (
            <div key={tipo} style={{ marginBottom: 12 }}>
              <div className="indicador-titulo">{ETIQUETA_TIPO[tipo]}</div>
              {lineas.map((l) => (
                <label
                  key={l.lineaOrigenId}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', minHeight: 44, cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={!excluidos.has(l.lineaOrigenId)}
                    onChange={(e) => alternar(l.lineaOrigenId, e.target.checked)}
                    style={{ width: 22, height: 22 }}
                  />
                  <span style={{ flex: 1 }}>
                    {l.nombre}
                    {l.nota ? <span className="campo-nota"> · {l.nota}</span> : null}
                  </span>
                  <span className="campo-nota">× {l.cantidad}</span>
                </label>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
